// Shared skeleton primitives. Senior-friendly: gentle shimmer, generous
// height, no flashing. CSS lives in styles/polish.css under `.skeleton`.
//
// Why a separate component instead of inline divs: the loading states on
// Interactions, Food, Reminders were "<p>Loading...</p>", which feels
// jarring at 65+ — the layout collapses and then snaps back. A structural
// skeleton in the shape of the real card preserves layout and reduces
// perceived wait.

interface SkeletonProps {
  /** CSS height; accepts px number or any CSS string. Default 16. */
  height?: number | string;
  /** CSS width; accepts % or px or any CSS string. Default '100%'. */
  width?: number | string;
  /** Border radius; default 6. Use 999 for pill/circle. */
  radius?: number;
  /** Top margin in px. Default 0. */
  marginTop?: number;
  /** Optional className for layout composition. */
  className?: string;
}

export function Skeleton({
  height = 16,
  width = '100%',
  radius = 6,
  marginTop = 0,
  className,
}: SkeletonProps): JSX.Element {
  return (
    <div
      className={`skeleton${className ? ` ${className}` : ''}`}
      aria-hidden="true"
      style={{
        height,
        width,
        borderRadius: radius,
        marginTop,
      }}
    />
  );
}

/**
 * Stack of N card-shaped skeletons, mimicking a generic list. Used by
 * Reminders, Doctors, and any future list page that's waiting on fetch.
 */
export function SkeletonCardList({ count = 3 }: { count?: number }): JSX.Element {
  return (
    <output aria-label="Loading" style={{ display: 'grid', gap: 12 }}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: stable count, no reordering
          key={i}
          style={{
            padding: 16,
            background: 'var(--color-surface)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border)',
          }}
        >
          <Skeleton height={20} width="55%" />
          <Skeleton height={14} width="80%" marginTop={10} />
          <Skeleton height={14} width="40%" marginTop={6} />
        </div>
      ))}
    </output>
  );
}

/**
 * Round node + connecting lines skeleton, shaped like the interaction
 * graph. Three nodes in a triangle, faint lines between them, all in the
 * shimmer state. Reads as "the graph is being built" without a spinner.
 */
export function SkeletonInteractionGraph(): JSX.Element {
  return (
    <output
      aria-label="Building your interaction graph"
      style={{
        display: 'block',
        position: 'relative',
        height: 260,
        margin: '16px 0',
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border)',
      }}
    >
      {/* Three node positions in a soft triangle */}
      {[
        { top: 30, left: '20%' },
        { top: 30, right: '20%' },
        { bottom: 30, left: '50%', transform: 'translateX(-50%)' },
      ].map((pos, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: stable count, no reordering
          key={i}
          className="skeleton"
          style={{
            position: 'absolute',
            width: 72,
            height: 72,
            borderRadius: '50%',
            ...pos,
          }}
        />
      ))}
      {/* Faint connector lines (rendered as thin rotated skeleton bars) */}
      <div
        className="skeleton"
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 70,
          left: '50%',
          width: '50%',
          height: 4,
          transform: 'translateX(-50%)',
          opacity: 0.4,
        }}
      />
    </output>
  );
}

/**
 * Two stacked skeleton paragraphs in the shape of a chat answer being
 * composed. Used by Food and any page that's waiting on a Gemini call.
 */
export function SkeletonAnswerBlock(): JSX.Element {
  return (
    <output
      aria-label="Loading"
      style={{
        padding: 16,
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border)',
        display: 'grid',
        gap: 8,
      }}
    >
      <Skeleton height={18} width="40%" />
      <Skeleton height={14} width="100%" marginTop={8} />
      <Skeleton height={14} width="92%" />
      <Skeleton height={14} width="74%" />
    </output>
  );
}
